import React, { useRef } from 'react';
import { FileDown, Printer, X } from 'lucide-react';
import useDialog from '../../hooks/useDialog';
import usePdfDownload from '../../hooks/usePdfDownload';
import { savePdf } from '../../utils/pdfDocument';
import { InvoiceReceiptSlip, printInvoiceReceipt, saveInvoiceReceiptPdf } from './InvoiceReceipt';
import PrintableInvoice from './PrintableInvoice';

/**
 * The receipt, shown before it prints, the way a token slip is: a preview of exactly what will come
 * out of the printer, then Print or Skip. It opens by itself after a payment (when the Billing
 * setting says so) and from Print receipt on a paid bill.
 *
 * Printing starts from the Print button, a click of the cashier's own, so a pop-up blocker has no
 * reason to refuse the print window. A clinic set to A4 sees the A4 page instead, which prints
 * through the browser's print of the page.
 */
const InvoiceReceiptModal = ({
  invoice,
  branding,
  paper = 'SLIP_80MM',
  isReprint = false,
  onClose,
  onPrintA4,
  onPrintBlocked,
}) => {
  const slipRef = useRef(null);
  const pageRef = useRef(null);
  const printButtonRef = useRef(null);
  const dialogRef = useDialog(onClose, { confirmDiscard: false, initialFocus: printButtonRef });
  const [downloading, download] = usePdfDownload();
  if (!invoice) return null;

  const a4 = paper === 'A4';
  const heading = isReprint ? 'Reprint Receipt' : 'Payment Received';
  const subheading = isReprint ? 'Print a copy for the patient' : 'Print receipt for patient';
  const dismissLabel = isReprint ? 'Close' : 'Skip Print';

  const print = () => {
    if (a4) {
      onClose();
      if (onPrintA4) onPrintA4();
      return;
    }
    if (!printInvoiceReceipt(slipRef.current, { title: `Receipt ${invoice.invoiceNumber || ''}`.trim() })) {
      if (onPrintBlocked) onPrintBlocked();
      return;
    }
    onClose();
  };

  // The preview as a PDF: the slip at 80 mm, or the A4 page, whichever this clinic prints. The dialog
  // stays open, so it can still be printed after.
  const savePreview = () => download(() => {
    const title = `Receipt ${invoice.invoiceNumber || ''}`.trim();
    const fileName = invoice.invoiceNumber || 'Receipt';
    return a4
      ? savePdf({ title, fileName, styles: 'body { font-family: Arial, Helvetica, sans-serif; }', bodyHtml: pageRef.current.innerHTML })
      : saveInvoiceReceiptPdf(slipRef.current, { title, fileName });
  });

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-receipt-title"
        className={`bg-white rounded-3xl shadow-2xl w-full overflow-hidden flex flex-col max-h-[92vh] ${a4 ? 'max-w-3xl' : 'max-w-md'}`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Printer className="h-5 w-5 text-white" aria-hidden="true" />
              </div>
              <div>
                <h2 id="invoice-receipt-title" className="text-xl font-bold text-white">{heading}</h2>
                <p className="text-emerald-100 text-sm">{subheading}</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Receipt preview */}
        <div className="p-6 overflow-y-auto">
          <div className="bg-gray-50 rounded-2xl p-4 border-2 border-dashed border-gray-200">
            {a4 ? (
              <div ref={pageRef}>
                <PrintableInvoice invoice={invoice} branding={branding} preview />
              </div>
            ) : (
              <div ref={slipRef}>
                <InvoiceReceiptSlip invoice={invoice} branding={branding} />
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
          >
            {dismissLabel}
          </button>
          <button
            type="button"
            onClick={savePreview}
            disabled={downloading}
            className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            <FileDown className="h-5 w-5" aria-hidden="true" />
            <span>{downloading ? 'Making the PDF…' : 'Download PDF'}</span>
          </button>
          <button
            ref={printButtonRef}
            type="button"
            onClick={print}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-emerald-800 transition-all flex items-center justify-center space-x-2 shadow-lg"
          >
            <Printer className="h-5 w-5" aria-hidden="true" />
            <span>Print Receipt</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceReceiptModal;
